export interface PositionLeg {
  position_id?: number;
  leg_id: number;
  sym: string;
  opt_sym?: string | null;
  strike: number;
  expiry: number; // epoch millis
  option_type: 'C' | 'P';
  quantity: number; // number of contracts
  price_paid: number; // price per contract
}

export interface PositionShares {
  position_id?: number;
  sym: string;
  quantity: number;
  price_paid: number;
}

export interface IVAdjustmentSettings {
  position_id: number;
  sym: string;
  expiry: number;
  adj_id?: number;
  level_shift: number;
  call_slope_mult: number;
  put_slope_mult: number;
}

export interface Position {
  id: number;
  name?: string;
  sym: string;
  option_legs: PositionLeg[];
  underlying_shares: PositionShares[];
  iv_adjustments: IVAdjustmentSettings[];
  created_at?: Date;
  updated_at?: Date;
  deletes?: PositionDeletes;
}

export interface PositionDeletes {
  adj_ids?: number[];
  leg_ids?: number[];
  underlying_share_syms?: string[];
}

export interface StrikeDeltaObject {
  strike: number;
  callDelta: number;
  putDelta: number;
}

export const OC_STORAGE_KEY = 'options_calculator_legs';
export const OC_SHARES_KEY = 'options_calculator_shares';
export const OC_SYM_KEY = 'options_calculator_sym';

export const OC_DEFAULT_SYM = 'SPX';
export const NEW_POSITION_ID: number = -1; // -1 so it doesn't conflict with any saved positions

export const OC_DEFAULT_LEVEL_SHIFT = 0;
export const OC_DEFAULT_CALL_SLOPE_MULT = 1;
export const OC_DEFAULT_PUT_SLOPE_MULT = 1;

export enum OCSettingsItem {
  RESTORE_DEFAULT_VIEW = 'restore_default_view',
  SMOOTHING = 'smoothing',
  KEY_LEVELS = 'key_levels',
  FULLSCREEN = 'fullscreen',
}

export interface OptionLegGreeks {
  delta: number;
  gamma: number;
  vega: number;
  theta: number;
  iv: number;
  adjustedIv?: number;
  price: number;
}

// Draft state management types
export interface OptionsCalculatorDraftState {
  // Draft for new position
  newPosition: Position;

  // Draft states for saved positions (keyed by position ID)
  savedPositionDrafts: Map<number, Position>;

  // Currently active position ID
  activePositionId: number;

  // Track which positions have unsaved changes
  hasUnsavedChanges: Map<number, boolean>;
}

export const OC_SAVED_POSITIONS_CACHE_KEY = 'saved_positions';

export interface StrategyTag {
  icon: React.ReactNode;
  text: string;
}

export enum StrategyType {
  CALL_CALENDAR_SPREAD = 'call_calendar_spread',
  CALL_DIAGONAL = 'call_diagonal',
  CALL_FLY = 'call_fly',
  CALL_VERTICAL = 'call_vertical',
  IRON_CONDOR = 'iron_condor',
  IRON_FLY = 'iron_fly',
  LONG_CALL = 'long_call',
  LONG_CALL_SPREAD = 'long_call_spread',
  LONG_PUT = 'long_put',
  LONG_PUT_SPREAD = 'long_put_spread',
  LONG_STRADDLE = 'long_straddle',
  PUT_CALENDAR_SPREAD = 'put_calendar_spread',
  PUT_DIAGONAL = 'put_diagonal',
  PUT_FLY = 'put_fly',
  PUT_VERTICAL = 'put_vertical',
  SHORT_CALL_SPREAD = 'short_call_spread',
  SHORT_PUT_SPREAD = 'short_put_spread',
}

export interface StrategyDefinition {
  id: StrategyType;
  name: string;
  description: string;
  tags: StrategyTag[];
  category: 'Directional' | 'Neutral' | 'Volatility';
  riskLevel: 'Low' | 'Medium' | 'High';
  legs: Array<{
    type: 'call' | 'put';
    action: 'buy' | 'sell';
    delta: number;
    dte: number;
  }>;
}

export type StrategyMirrorLeg = {
  quantity?: number;
  optionType: 'C' | 'P';
  target: 'fwdPrice' | { index: number };
};

export type StrategyLeg = {
  delta: number;
  dte: number;
  quantity: number;
  optionType: 'C' | 'P';
  mirror?: StrategyMirrorLeg;
};

export type StrategyLegs = Record<StrategyType, Array<StrategyLeg>>;

export const PNL_DATA_IDX = {
  PRICE: 0,
  PROFIT: 1,
  DELTA: 2,
  GAMMA: 3,
  VEGA: 4,
  THETA: 5,
};

export interface PnlSummary {
  maxProfit: number;
  maxLoss: number;
  breakEvenPrices: number[];
  totalDelta: number;
}

// this is copied from the middle-layer schema
// WARNING: Using the `number` for position_id and leg_id will break once we
//          pass values exceeding Number.MAX_SAFE_INTEGER (9e15).
interface OptionLegDB {
  position_id?: number;
  leg_id?: number;
  sym: string;
  opt_sym?: string | null;
  strike: number;
  expiry: Date; // epoch millis
  option_type: string;
  quantity: number; // number of contracts
  price_paid: number; // price per contract
}

interface UnderlyingSharesDB {
  position_id?: number;
  sym: string;
  quantity: number;
  price_paid: number;
}

interface IVAdjustmentDB {
  position_id?: number;
  adj_id?: number;
  sym: string;
  expiry: Date;
  level_shift: number;
  call_slope_mult: number;
  put_slope_mult: number;
}

interface PositionDeletesDB {
  adj_ids?: number[];
  leg_ids?: number[];
  underlying_share_syms?: string[];
}

export interface PositionDB {
  id?: number;
  name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  option_legs: OptionLegDB[];
  underlying_shares: UnderlyingSharesDB[];
  iv_adjustments: IVAdjustmentDB[];
  deletes?: PositionDeletesDB;
}

export interface D3PnlPlotDataPointAttribute {
  label: string;
  value: number;
  color: string;
}

export interface D3PnlPlotDataPoint {
  x: number;
  y: number;
  attributes: D3PnlPlotDataPointAttribute[];
}

export interface D3PnlPlotLine {
  data: D3PnlPlotDataPoint[];
  stroke: string;
  strokeWidth?: number;
  fill?: string;
  strokeDasharray?: string;
  opacity?: number;
  label?: string;
}

export interface D3PnlPlotArea {
  data: number[][];
  fill: string;
  opacity: number;
}
